import { flatMap, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import {
  AnalogJoint,
  AnalogLink,
  AnalogModel,
  AnalysisResult,
  Member,
  PlatePair,
  ResultJointGuid,
  ResultLinkGuid,
} from '@paragon/contract-library';

import { getAnalogModelForActiveComponentDesign } from './getAnalogModelForActiveComponentDesignSelector';
import { getSelectedAnalysisResult } from './getSelectedAnalysisResultSelector';
import { getSingleSelectedItemEntryOrNull } from './getSelectedItemSelector';

const makeActiveSubResultsSelector = <K extends keyof AnalysisResult>(field: K) =>
  createSelector(getSelectedAnalysisResult, (analysisResult) => analysisResult?.[field] ?? null);
export const getActiveLinkEndResults = makeActiveSubResultsSelector('linkEndResults');
export const getActivePlatingForceResults = makeActiveSubResultsSelector('platingForceResults');
export const getActiveBearingReactions = makeActiveSubResultsSelector('analogJointResults');

const makeActiveAnalysisResultCurveSelector = (curveType: string) =>
  createSelector(
    getSelectedAnalysisResult,
    (analysisResult) =>
      analysisResult?.analogLinkResults.filter((linkResult) => linkResult.curveType === curveType) ?? null,
  );
export const getActiveMomentCurves = makeActiveAnalysisResultCurveSelector('Moment');
export const getActiveDeflectionCurves = makeActiveAnalysisResultCurveSelector('Deflection');
export const getActiveShearCurves = makeActiveAnalysisResultCurveSelector('Shear');

const getComponentResults = <T>(results: readonly T[]) => results;

const getJointResults = <T extends ResultJointGuid>(results: readonly T[], joint: AnalogJoint) =>
  results.filter((result) => result.analogJointGuid === joint.guid);

const getLinkResultsFromLinkGuid = <T extends ResultLinkGuid>(results: readonly T[], link: AnalogLink) =>
  results.filter((result) => result.analogLinkGuid === link.guid);

const getLinkResultsFromJointGuid = <T extends ResultJointGuid>(results: readonly T[], link: AnalogLink) =>
  results.filter(
    (result) => result.analogJointGuid === link.iJoint.guid || result.analogJointGuid === link.jJoint.guid,
  );

const getMemberResultsFromLinkGuid = <T extends ResultLinkGuid>(
  results: readonly T[],
  member: Member,
  analyzedAnalogModel: AnalogModel | null,
): T[] => {
  if (analyzedAnalogModel === null) return [];
  const associatedLinkGuids = analyzedAnalogModel.analogLinks
    .filter((link) => link.memberStack.memberGuids.includes(member.guid))
    .map((link) => link.guid);
  return results.filter((result) => associatedLinkGuids.includes(result.analogLinkGuid));
};

const getMemberResultsFromJointGuid = <T extends ResultJointGuid>(
  results: readonly T[],
  member: Member,
  analyzedAnalogModel: AnalogModel | null,
): T[] => {
  if (analyzedAnalogModel === null) return [];
  const associatedLinks = analyzedAnalogModel.analogLinks.filter((link) =>
    link.memberStack.memberGuids.includes(member.guid),
  );
  const associatedJointGuids = uniq(flatMap(associatedLinks, (link) => [link.iJoint.guid, link.jJoint.guid]));
  return results.filter((result) => associatedJointGuids.includes(result.analogJointGuid));
};

// Heuristic: plate names are typically the joint number plus an optional suffix ("21A" → "21").
// This is based on current naming conventions, not a guaranteed contract — avoid relying on it too heavily.
export const getJointNameFromPlateName = (plateName: string): string => plateName.match(/^\d+/)?.[0] ?? plateName;

const getPlatePairResults = <T extends ResultJointGuid>(
  results: readonly T[],
  platePair: PlatePair,
  analyzedAnalogModel: AnalogModel | null,
): T[] => {
  if (analyzedAnalogModel === null) return [];
  const associatedJointName = getJointNameFromPlateName(platePair.name);
  const associatedJoint = analyzedAnalogModel.analogJoints
    .filter((joint) => joint.analogModelGuid === analyzedAnalogModel.guid)
    .find((joint) => joint.name === associatedJointName);
  if (associatedJoint === undefined) return [];
  return results.filter((result) => result.analogJointGuid === associatedJoint.guid);
};

export const getJointResultsForSelectedLoadCaseAndSelectedItem = createSelector(
  getSingleSelectedItemEntryOrNull,
  getActiveBearingReactions,
  getAnalogModelForActiveComponentDesign,
  (selectedEntry, activeJointResults, analogModel) => {
    if (selectedEntry === null || activeJointResults == null) return [];
    switch (selectedEntry.type) {
      case 'componentDesign':
        return getComponentResults(activeJointResults);
      case 'analogJoint':
        return getJointResults(activeJointResults, selectedEntry.item);
      case 'analogLink':
        return getLinkResultsFromJointGuid(activeJointResults, selectedEntry.item);
      case 'member':
        return getMemberResultsFromJointGuid(activeJointResults, selectedEntry.item, analogModel);
      case 'platePair':
        return getPlatePairResults(activeJointResults, selectedEntry.item, analogModel);
      default:
        return [];
    }
  },
);

export const getLinkEndResultsForSelectedLoadCaseAndSelectedItem = createSelector(
  getSingleSelectedItemEntryOrNull,
  getActiveLinkEndResults,
  getAnalogModelForActiveComponentDesign,
  (selectedEntry, activeLinkEndResults, analyzedAnalogModel) => {
    if (selectedEntry === null || activeLinkEndResults == null) return [];
    switch (selectedEntry.type) {
      case 'componentDesign':
        return getComponentResults(activeLinkEndResults);
      case 'analogJoint':
        return getJointResults(activeLinkEndResults, selectedEntry.item);
      case 'analogLink':
        return getLinkResultsFromLinkGuid(activeLinkEndResults, selectedEntry.item);
      case 'member':
        return getMemberResultsFromLinkGuid(activeLinkEndResults, selectedEntry.item, analyzedAnalogModel);
      case 'platePair':
        return getPlatePairResults(activeLinkEndResults, selectedEntry.item, analyzedAnalogModel);
      default:
        return [];
    }
  },
);

export const getPlatingForcesForSelectedLoadCaseAndSelectedItem = createSelector(
  getSingleSelectedItemEntryOrNull,
  getActivePlatingForceResults,
  getAnalogModelForActiveComponentDesign,
  (selectedEntry, activePlatingForceResults, analyzedAnalogModel) => {
    if (selectedEntry === null || activePlatingForceResults == null) return [];
    switch (selectedEntry.type) {
      case 'componentDesign':
        return getComponentResults(activePlatingForceResults);
      case 'analogJoint':
        return getJointResults(activePlatingForceResults, selectedEntry.item);
      case 'analogLink':
        return getLinkResultsFromJointGuid(activePlatingForceResults, selectedEntry.item);
      case 'member':
        return getMemberResultsFromJointGuid(activePlatingForceResults, selectedEntry.item, analyzedAnalogModel);
      case 'platePair':
        return getPlatePairResults(activePlatingForceResults, selectedEntry.item, analyzedAnalogModel);
      default:
        return [];
    }
  },
);

export const getMomentCurvesForSelectedLoadCaseAndSelectedItem = createSelector(
  getSingleSelectedItemEntryOrNull,
  getActiveMomentCurves,
  getAnalogModelForActiveComponentDesign,
  (selectedEntry, activeMomentCurveResults, analyzedAnalogModel) => {
    if (selectedEntry === null || activeMomentCurveResults == null) return [];
    switch (selectedEntry.type) {
      case 'componentDesign':
        return getComponentResults(activeMomentCurveResults);
      case 'analogLink':
        return getLinkResultsFromLinkGuid(activeMomentCurveResults, selectedEntry.item);
      case 'member':
        return getMemberResultsFromLinkGuid(activeMomentCurveResults, selectedEntry.item, analyzedAnalogModel);
      default:
        return [];
    }
  },
);

export const getDeflectionCurvesForSelectedLoadCaseAndSelectedItem = createSelector(
  getSingleSelectedItemEntryOrNull,
  getActiveDeflectionCurves,
  getAnalogModelForActiveComponentDesign,
  (selectedEntry, activeDeflectionCurveResults, analyzedAnalogModel) => {
    if (selectedEntry === null || activeDeflectionCurveResults == null) return [];
    switch (selectedEntry.type) {
      case 'componentDesign':
        return getComponentResults(activeDeflectionCurveResults);
      case 'analogLink':
        return getLinkResultsFromLinkGuid(activeDeflectionCurveResults, selectedEntry.item);
      case 'member':
        return getMemberResultsFromLinkGuid(activeDeflectionCurveResults, selectedEntry.item, analyzedAnalogModel);
      default:
        return [];
    }
  },
);

export const getShearCurvesForSelectedLoadCaseAndSelectedItem = createSelector(
  getSingleSelectedItemEntryOrNull,
  getActiveShearCurves,
  getAnalogModelForActiveComponentDesign,
  (selectedEntry, activeShearCurveResults, analyzedAnalogModel) => {
    if (selectedEntry === null || activeShearCurveResults == null) return [];
    switch (selectedEntry.type) {
      case 'componentDesign':
        return getComponentResults(activeShearCurveResults);
      case 'analogLink':
        return getLinkResultsFromLinkGuid(activeShearCurveResults, selectedEntry.item);
      case 'member':
        return getMemberResultsFromLinkGuid(activeShearCurveResults, selectedEntry.item, analyzedAnalogModel);
      default:
        return [];
    }
  },
);
